import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { ShieldCheck, Route, Layers, FileCheck2 } from "lucide-react";
import { MetricCard, GoldButton } from "@/components/ds";

const METRICS = [
  { icon: ShieldCheck, value: "FATF", label: "Aligned Compliance", sub: "OECD DDG · sanctions-screened network", id: "compliance" },
  { icon: Route, value: "3", label: "Execution Corridors", sub: "East & Central Africa · West Africa · Gulf", id: "corridors" },
  { icon: Layers, value: "12+", label: "Verified Commodities", sub: "Two groups · precious & industrial minerals", id: "commodities" },
  { icon: FileCheck2, value: "100%", label: "Counterparty KYC/AML", sub: "No unverified principal advances", id: "kyc" },
];

const LINES = [
  { text: "Real Supply.", cls: "text-white" },
  { text: "Verified Counterparties.", cls: "gold-text" },
  { text: "Disciplined Execution.", cls: "text-white" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const driftX = useSpring(0, { stiffness: 38, damping: 18 });
  const driftY = useSpring(0, { stiffness: 38, damping: 18 });

  const onMouseMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    driftX.set(((e.clientX - r.left) / r.width - 0.5) * 16);
    driftY.set(((e.clientY - r.top) / r.height - 0.5) * 10);
  };

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMouseMove}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-end overflow-hidden"
    >
      <motion.div className="absolute inset-0" style={{ y: parallaxY }}>
        <motion.img
          src="/assets/hero-bars.webp"
          alt="Stacked gold bullion bars in a dark vault"
          className="w-full h-full object-cover object-right"
          style={{ x: driftX, y: driftY, scale: 1.07 }}
          fetchPriority="high"
          width="1920"
          height="480"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0B] via-[#0A0A0B]/72 to-[#0A0A0B]/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-transparent to-[#0A0A0B]/60" />
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full pt-44 pb-16">
        <div className="max-w-3xl">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="font-mono2 text-[11px] uppercase tracking-[0.35em] text-[#C9A227] block mb-6"
          >
            Facilitator, Not Broker · Phoenix, Arizona
          </motion.span>

          <h1
            data-testid="hero-headline"
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium uppercase leading-[1.12] tracking-wide"
            aria-label="Real Supply. Verified Counterparties. Disciplined Execution."
          >
            {LINES.map((l, i) => (
              <span key={i} className="block overflow-hidden pb-1" aria-hidden="true">
                <motion.span
                  className={`block ${l.cls}`}
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1.05, delay: 0.3 + i * 0.16, ease: [0.22, 1, 0.36, 1] }}
                >
                  {l.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 h-px w-40 origin-left bg-gradient-to-r from-[#C9A227] to-transparent"
          />

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-6 text-base md:text-lg text-zinc-400 leading-relaxed max-w-xl"
          >
            Zorem Commodities LLC is the disciplined conduit through which the
            world's mineral resources flow — meticulously verified, and delivered
            with absolute integrity. Structured transactions only. No informal channels.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.25 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <GoldButton to="/mandate" testid="hero-cta-primary">Submit a Mandate</GoldButton>
            <GoldButton href="#commodities" variant="ghost" testid="hero-cta-secondary">Explore Commodities</GoldButton>
          </motion.div>
        </div>

        <div className="mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#C9A227]/15 card-line" data-testid="hero-metrics">
          {METRICS.map((m, i) => (
            <MetricCard key={m.id} {...m} testid={`metric-card-${m.id}`} delay={1.3 + i * 0.12} />
          ))}
        </div>
      </div>
    </section>
  );
};
